/**
 * Table sub-components for the Import Dashboard.
 *
 * Extracted from ImportDashboard.tsx to keep files under 500 lines.
 */
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Pagination } from '../../components/ui/pagination';
import { ChevronRight, Search, Loader2 } from 'lucide-react';
import type { ImportJob, ImportConfiguration } from '../../lib/api/imports';

// ---------------------------------------------------------------------------
// Shared helpers (duplicated from parent — kept minimal to avoid circular deps)
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
  completed: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  failed: { color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  partial: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  running: { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  pending: { color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
  cancelled: { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
    </span>
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Jobs Table
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'partial', label: 'Partial' },
  { value: 'running', label: 'Running' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface JobsTableProps {
  jobs: ImportJob[];
  onSelectJob: (jobId: string) => void;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  search: string;
  onSearchChange: (search: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  dateFrom: string;
  onDateFromChange: (date: string) => void;
  dateTo: string;
  onDateToChange: (date: string) => void;
  /** Cancel a running/pending job (revokes the task + marks it failed). */
  onCancelJob?: (jobId: string) => void;
  /** job_id currently being cancelled (shows a spinner on that row). */
  cancellingJobId?: string | null;
}

export function JobsTable({
  jobs,
  onSelectJob,
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  onCancelJob,
  cancellingJobId,
}: JobsTableProps) {
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <Card>
      {/* Search & filter bar */}
      <div className="flex items-center gap-3 p-4 border-b">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by job ID, data type, customer, or error..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <input
            type="date"
            aria-label="From date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <input
            type="date"
            aria-label="To date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => onDateToChange(e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { onDateFromChange(''); onDateToChange(''); }}
              className="text-sm text-muted-foreground hover:text-foreground px-2"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {jobs.length === 0 ? (
        <CardContent className="py-12 text-center text-muted-foreground">
          {search || statusFilter ? 'No jobs match your filters' : 'No import jobs found'}
        </CardContent>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Data Type</th>
                  <th className="text-left p-3 font-medium">Customer</th>
                  <th className="text-left p-3 font-medium">Source</th>
                  <th className="text-right p-3 font-medium">Total</th>
                  <th className="text-right p-3 font-medium">Created</th>
                  <th className="text-right p-3 font-medium">Updated</th>
                  <th className="text-right p-3 font-medium">Skipped</th>
                  <th className="text-right p-3 font-medium">Unchanged</th>
                  <th className="text-right p-3 font-medium">Failed</th>
                  <th className="text-right p-3 font-medium">Duration</th>
                  <th className="text-left p-3 font-medium">When</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => onSelectJob(job.job_id)}>
                    <td className="p-3"><StatusBadge status={job.status} /></td>
                    <td className="p-3 font-medium">{job.data_type_display}</td>
                    <td className="p-3 text-muted-foreground">{job.supplier_name}</td>
                    <td className="p-3 text-muted-foreground">{job.source_display}</td>
                    <td className="p-3 text-right tabular-nums">{job.total_records.toLocaleString()}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-600">{job.records_created.toLocaleString()}</td>
                    <td className="p-3 text-right tabular-nums">
                      {job.records_updated > 0 ? (
                        <span className="text-blue-600">{job.records_updated.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {job.records_skipped > 0 ? (
                        <span className="text-amber-600">{job.records_skipped.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{job.records_unchanged.toLocaleString()}</td>
                    <td className="p-3 text-right tabular-nums">
                      {job.records_failed > 0 ? (
                        <span className="text-red-600">{job.records_failed.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{formatDuration(job.duration_seconds)}</td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">{timeAgo(job.created_at)}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {onCancelJob && (job.status === 'running' || job.status === 'pending') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            disabled={cancellingJobId === job.job_id}
                            onClick={(e) => { e.stopPropagation(); onCancelJob(job.job_id); }}
                          >
                            {cancellingJobId === job.job_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Cancel'}
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              itemLabel="jobs"
            />
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Config Table
// ---------------------------------------------------------------------------

interface ConfigTableProps {
  configurations: ImportConfiguration[];
}

export function ConfigTable({ configurations }: ConfigTableProps) {
  if (configurations.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No import configurations found
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-3 font-medium">Customer</th>
              <th className="text-left p-3 font-medium">Data Type</th>
              <th className="text-left p-3 font-medium">Schedule</th>
              <th className="text-center p-3 font-medium">Active</th>
              <th className="text-right p-3 font-medium">Batch Size</th>
              <th className="text-left p-3 font-medium">Last Run</th>
              <th className="text-left p-3 font-medium">Last Success</th>
              <th className="text-right p-3 font-medium">Failures</th>
            </tr>
          </thead>
          <tbody>
            {configurations.map(cfg => (
              <tr key={cfg.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                <td className="p-3 font-medium">{cfg.supplier_name}</td>
                <td className="p-3">{cfg.data_type_display}</td>
                <td className="p-3 text-muted-foreground">
                  {cfg.schedule_type_display}
                  {cfg.cron_expression && <span className="ml-1 text-xs font-mono">({cfg.cron_expression})</span>}
                </td>
                <td className="p-3 text-center">
                  {cfg.is_active ? (
                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" title="Active" />
                  ) : (
                    <span className="inline-block h-2 w-2 rounded-full bg-slate-300" title="Inactive" />
                  )}
                </td>
                <td className="p-3 text-right tabular-nums">{cfg.batch_size}</td>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{timeAgo(cfg.last_run_at)}</td>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{timeAgo(cfg.last_success_at)}</td>
                <td className="p-3 text-right">
                  {cfg.consecutive_failures > 0 ? (
                    <Badge variant="destructive" className="text-xs">{cfg.consecutive_failures}</Badge>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
